import React from 'react';
import {
  FormField,
  FormGroupCard,
  SwitchToggle,
  VariantOptionsBuilder,
} from 'src/shared/forms/components';
import type { WizardStepComponentProps } from 'src/shared/forms/types';
import type { 
  ProductWizardData
} from '../../../hooks/useProductWizard';
import { WarehouseStockManager } from '../components/WarehouseStockManager';

interface SalesInfoStepProps extends WizardStepComponentProps {
  data: ProductWizardData['salesInfo'];
}

const SalesInfoStep: React.FC<SalesInfoStepProps> = ({
  data,
  onDataChange,
  allData,
  errors = []
}) => {

  const updateSalesData = (updates: Partial<ProductWizardData['salesInfo']>) => {
    onDataChange({
      ...data,
      ...updates
    });
  };

  const updateInventory = (updates: Partial<ProductWizardData['salesInfo']['inventory']>) => {
    updateSalesData({
      inventory: {
        ...(data.inventory || { quantity: 0, trackQuantity: true }),
        ...updates,
        trackQuantity: updates.trackQuantity ?? data.inventory?.trackQuantity ?? true
      }
    });
  };

  const basicInfo = allData?.basicInfo || {};

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 md:tw-p-6 tw-shadow-sm" data-testid="wizard-step-salesInfo">
      <div className="tw-mb-6 tw-border-b tw-border-gray-200 tw-pb-4">
        <h3 className="tw-text-lg tw-font-bold tw-text-gray-900">
          Sales Information
        </h3>
        <p className="tw-text-sm tw-text-gray-500">
          Set pricing, inventory, and manage product variants.
        </p>
      </div>
      <div className="tw-grid tw-gap-6">
        {/* Minimum Purchase (Always visible) */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6" data-testid="wizard-salesInfo-minimumPurchase">
          <FormField
            name="minPurchaseQuantity"
            label="Minimum Purchase Quantity"
            helpText="Minimum quantity customers must purchase."
          >
            <input
              type="number"
              value={data.minPurchaseQuantity?.toString() || "1"}
              onChange={(e) =>
                updateSalesData({
                  minPurchaseQuantity: parseInt(e.target.value) || 1,
                })
              }
              placeholder="1"
              min="1"
              step="1"
              className="tw-w-full tw-p-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
            />
          </FormField>
        </div>

        {/* Has Variants Toggle */}
        <div data-testid="wizard-salesInfo-variantsToggle">
          <SwitchToggle
            title="Product Variants"
            description="Does this product have variants like size, color, or material?"
            checked={data.hasVariants || false}
            onChange={(hasVariants) => {
              updateSalesData({
                hasVariants,
                // Clear variant data when disabling variants
                ...(hasVariants
                  ? {}
                  : { variantOptions: [], variants: [] }),
              });
            }}
          />
        </div>

        {/* Pricing Information Group (shown when no variants) */}
        {!data.hasVariants && (
          <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 md:tw-p-6 tw-shadow-sm" data-testid="wizard-salesInfo-pricing-group">
            <div className="tw-mb-6 tw-border-b tw-border-gray-200 tw-pb-4">
              <h4 className="tw-text-lg tw-font-bold tw-text-gray-900">
                Pricing Information
              </h4>
              <p className="tw-text-sm tw-text-gray-500">
                Set regular price and optional sale price for your
                product.
              </p>
            </div>
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
              <FormField
                name="regularPrice"
                label="Regular Price"
                required
                error={errors.find(e => e.includes('price'))}
                helpText="The main selling price for this product."
              >
                <div className="tw-relative">
                  <span className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-500 tw-text-sm">
                    Rp
                  </span>
                  <input
                    type="number"
                    value={data.basePrice?.toString() || ""}
                    onChange={(e) =>
                      updateSalesData({
                        basePrice: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="tw-w-full tw-pl-12 tw-pr-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
              </FormField>

              <FormField
                name="salePrice"
                label="Sale Price"
                helpText="Optional discounted price. Leave empty for no discount."
              >
                <div className="tw-relative">
                  <span className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-500 tw-text-sm">
                    Rp
                  </span>
                  <input
                    type="number"
                    value={data.comparePrice?.toString() || ""}
                    onChange={(e) =>
                      updateSalesData({
                        comparePrice: parseFloat(e.target.value) || undefined,
                      })
                    }
                    className="tw-w-full tw-pl-12 tw-pr-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
              </FormField>
            </div>
          </div>
        )}

        {/* Inventory Management Group (shown when no variants) */}
        {!data.hasVariants && (
          <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 md:tw-p-6 tw-shadow-sm" data-testid="wizard-salesInfo-inventory-group">
            <div className="tw-mb-6 tw-border-b tw-border-gray-200 tw-pb-4">
              <h4 className="tw-text-lg tw-font-bold tw-text-gray-900">
                Inventory Management
              </h4>
              <p className="tw-text-sm tw-text-gray-500">
                Manage stock quantity across different warehouse
                locations.
              </p>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2">
              <FormField
                name="stockQuantity"
                label="Stock Quantity"
                helpText="Total available inventory for this product."
              >
                <input
                  type="number"
                  value={data.inventory?.quantity?.toString() || ""}
                  onChange={(e) =>
                    updateInventory({
                      quantity: parseInt(e.target.value) || 0,
                    })
                  }
                  className="tw-w-full tw-p-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                  placeholder="0"
                  min="0"
                  step="1"
                />
              </FormField>

              {/* Warehouse Stock Management */}
              <WarehouseStockManager
                storeId={basicInfo.selectedStore}
                totalStock={data.inventory?.quantity || 0}
                inventoryAllocation={data.inventory?.inventoryAllocation || {}}
                onInventoryAllocationChange={(inventoryAllocation) =>
                  updateInventory({ inventoryAllocation })
                }
              />
            </div>
          </div>
        )}

        {/* Variant Management (shown when has variants) */}
        {data.hasVariants && (
          <div className="tw-space-y-6">
            {/* Variant Options Builder */}
            <VariantOptionsBuilder
              options={data.variantOptions || []}
              variants={data.variants || []}
              onOptionsChange={(options) => {
                // Update wizard state with new options
                updateSalesData({
                  variantOptions: options,
                });
              }}
              onVariantsChange={(variants) => {
                // Update wizard state with new variants
                updateSalesData({ variants });
              }}
              productContext={{
                id: basicInfo.masterSku || "temp-product-id",
                name: basicInfo.name,
                category: allData?.category?.categoryId,
                brand: allData?.category?.brandId,
                description: basicInfo.description,
                linkedMerchantProduct: basicInfo.linkedMerchantProduct,
              }}
              basePrice={data.basePrice}
              defaultStock={data.inventory?.quantity || 0}
              autoGenerate={false}
              showBulkOperations={false}
              className="tw-w-full"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesInfoStep;